import { useServices } from "@/services/hooks/useServices";
import {
  DowngradePlanDtoType,
  ICancelSenderProfileSubscriptionPayload,
  ICreateCheckoutAddonPayload,
  ICreateSenderProfileSubscriptionPayload,
  ICustomPlanRequestPayload,
  IPaymentMethodResponse,
  IPlanCancellationRequestPayload,
  IPurchasePlanPayload,
  IUpgradePlanPayload,
  TestAddonType,
  UpdatePlanDtoType,
} from "@/types/subscription.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const SUBSCRIPTION_QUERY_KEY = "subscription";
export const USAGE_QUERY_KEY = "usage";
export const INVOICES_QUERY_KEY = "invoices";
export const CANCELLATION_QUERY_KEY = "cancellation";
export const ADDON_TEST = "addon-test";

// -------------------- Retrieval Methods --------------------

export const useGetUsage = () => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [USAGE_QUERY_KEY],
    queryFn: () => subscriptionService.getUsage(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

// current subscription plans
export const useGetActiveSubscriptionPlans = () => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
    queryFn: () => subscriptionService.getCurrentSubscriptionPlans(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const usePurchaseSubscriptionSession = () => {
  const { subscriptionService } = useServices();
  return useMutation({
    mutationFn: (data: IPurchasePlanPayload) =>
      subscriptionService.createCheckoutSession(data),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
};

export const usePurchaseNewPlan = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IPurchasePlanPayload) =>
      subscriptionService.buyNewPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });

      queryClient.invalidateQueries({
        queryKey: [USAGE_QUERY_KEY],
      });

      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY],
      });
    },
  });
};

export const useGetPaymentMethods = () => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY, "payment-methods"],
    queryFn: () => subscriptionService.getPaymentMethods(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useUpdateDefaultPaymentMethod = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      subscriptionService.updateDefaultPaymentMethod(paymentMethodId),
    onSuccess: (_data, paymentMethodId) => {
      // optimistic update

      queryClient.setQueryData(
        [SUBSCRIPTION_QUERY_KEY, "payment-methods"],
        (oldData: IPaymentMethodResponse) => {
          return {
            ...oldData,
            defaultPaymentMethodId: paymentMethodId,
          };
        }
      );
    },
  });
};

export const useGetInvoices = ({
  startingAfter,
}: {
  startingAfter?: string | null;
}) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY, { startingAfter }],
    queryFn: () => subscriptionService.getInvoices(startingAfter ?? undefined),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useAddPaymentMethod = () => {
  const { subscriptionService } = useServices();
  return useMutation({
    mutationFn: () => subscriptionService.createPaymentStepSession(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
};

export const useDeletePaymentMethod = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      subscriptionService.deletePaymentMethod(paymentMethodId),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(
        [SUBSCRIPTION_QUERY_KEY, "payment-methods"],
        (oldData: IPaymentMethodResponse) => {
          return {
            ...oldData,
            paymentMethods: oldData.paymentMethods.filter(
              (method) => method.id !== variables
            ),
          };
        }
      );
    },
  });
};

export const useBuyMoreMailboxes = () => {
  const queryClient = useQueryClient();
  const { subscriptionService } = useServices();
  return useMutation({
    mutationFn: (data: {
      subscriptionId: string;
      planId: string;
      quantity: number;
    }) =>
      subscriptionService.buyMoreMailboxes(
        data.subscriptionId,
        data.planId,
        data.quantity
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [USAGE_QUERY_KEY],
      });
    },
  });
};

export const useAssignPlan = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      subscriptionId: string;
      mailboxId: string;
      planId: string;
    }) =>
      subscriptionService.assignPlan(
        data.subscriptionId,
        data.mailboxId,
        data.planId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useUnassignPlan = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      subscriptionId: string;
      mailboxId: string;
      planId: string;
    }) =>
      subscriptionService.unassignPlan(
        data.subscriptionId,
        data.mailboxId,
        data.planId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useCustomerPortal = () => {
  const { subscriptionService } = useServices();
  return useMutation({
    mutationFn: () => subscriptionService.createCustomerPortalSession(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
};

export const useGetMailboxesForPlan = ({
  subscriptionId,
  planId,
}: {
  subscriptionId: string;
  planId: string;
}) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [
      SUBSCRIPTION_QUERY_KEY,
      "mailboxes-for-plan",
      { subscriptionId, planId },
    ],
    queryFn: () =>
      subscriptionService.getMailboxesForPlan(subscriptionId, planId),

    refetchOnWindowFocus: false,
    enabled: !!subscriptionId && !!planId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpgradePlan = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePlanDtoType) =>
      subscriptionService.upgradePlan(data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });

      queryClient.invalidateQueries({
        queryKey: [USAGE_QUERY_KEY],
      });

      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY],
      });
    },
  });
};

export const useGetAllowedEspTypes = () => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: ["allowed-esp-types"],
    queryFn: () => subscriptionService.getAllowedEspTypes(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSendDowngradeRequest = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { reasons: string[]; additionalFeedback?: string }) =>
      subscriptionService.sendDowngradeFeedback(
        data.reasons,
        data.additionalFeedback
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useSendPlanCancellationRequest = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IPlanCancellationRequestPayload) =>
      subscriptionService.sendPlanCancellationRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useGetPlanMetrics = (planId: string) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY, "plan-metrics", planId],
    queryFn: () => subscriptionService.getPlanMetrics(planId),
    refetchOnWindowFocus: false,
    enabled: !!planId,
  });
};

export const useGetPlanDetails = (planType: string, options = {}) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: ["plan-details", planType],
    queryFn: async () => {
      if (!planType) return null;
      return await subscriptionService.getPlanDetailsByType(planType);
    },
    enabled: !!planType,
    ...options,
  });
};

export const useGetPlanComparisonData = (
  currentPlanId: string,
  targetPlanType: string
) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [
      SUBSCRIPTION_QUERY_KEY,
      "plan-comparison",
      currentPlanId,
      targetPlanType,
    ],
    queryFn: () =>
      subscriptionService.getPlanComparisonData(currentPlanId, targetPlanType),
    refetchOnWindowFocus: false,
    enabled: !!currentPlanId && !!targetPlanType,
  });
};

export const useGetPendingCancellations = () => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [CANCELLATION_QUERY_KEY, "pending-cancellations"],
    queryFn: () => subscriptionService.getPendingCancellations(),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGetPendingCancellationForPlan = (planId: string) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [CANCELLATION_QUERY_KEY, "pending-cancellation", planId],
    queryFn: () => subscriptionService.getPendingCancellationForPlan(planId),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!planId,
  });
};

export const useRevokeCancellation = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cancellationId: string) =>
      subscriptionService.revokeCancellation(cancellationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useRevokeDowngrade = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (downgradeId: string) =>
      subscriptionService.revokeDowngrade(downgradeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useGetAddonPlan = (planId: string, enabled = true) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY, "addon-plan", planId],
    queryFn: () => subscriptionService.getAddonPlan(planId),
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useDowngradePlan = () => {
  const { subscriptionService } = useServices();
  return useMutation({
    mutationFn: (data: DowngradePlanDtoType) =>
      subscriptionService.downgradePlan(data),
  });
};

export const useGetAddonTestPricing = (type: TestAddonType) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [ADDON_TEST, "pricing", type],
    queryFn: () => subscriptionService.getAddonTestPricing(type),
    enabled: !!type,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useBuyAddonTests = () => {
  const { subscriptionService } = useServices();

  return useMutation({
    mutationFn: (data: ICreateCheckoutAddonPayload) =>
      subscriptionService.createAddonTestSession(data),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
};

export const useCreateSenderProfileSubscription = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ICreateSenderProfileSubscriptionPayload) =>
      subscriptionService.createSenderProfileSubscription(data),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else {
        // If no URL, invalidate queries to refresh subscription data
        queryClient.invalidateQueries({
          queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
        });
      }
    },
  });
};

export const useGetSenderProfilePlans = () => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY, "sender-profile-plans"],
    queryFn: () => subscriptionService.getSenderProfilePlans(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useGetPlanDetail = (
  subscriptionId: string | undefined,
  planId: string | undefined
) => {
  const { subscriptionService } = useServices();
  return useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY, "plan-detail", subscriptionId, planId],
    queryFn: () =>
      subscriptionService.getPlanDetail(subscriptionId!, planId!),
    refetchOnWindowFocus: false,
    enabled: !!subscriptionId && !!planId,
    staleTime: Infinity,
  });
};

export const useCancelSenderProfileSubscription = () => {
  const { subscriptionService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICancelSenderProfileSubscriptionPayload) =>
      subscriptionService.cancelSenderProfileSubscription(payload),
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "sender-profile-plans"],
      });
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"],
      });
    },
  });
};

export const useCreateCustomPlanRequest = () => {
  const { subscriptionService } = useServices();
  return useMutation({
    mutationFn: (payload: ICustomPlanRequestPayload) =>
      subscriptionService.createCustomPlanRequest(payload),
  });
};